/*
 * Copyright (c) 2024 Amazon.com, Inc. All Rights Reserved.
 */
import {AntiSignatureEntry, AntiSignatureFeatures, AntiSignatureType, convertTtlToDaysRemaining} from "../../model/AntiSignatureEntry";
import {AntiSignatureButtons} from "./AntiSignatureButtons";
import React, {useRef, useState} from "react";
import {Region} from "../../model/Region";
import {useOutsideClick} from "../../hooks/useOutsideClick";
import {IoClose} from "react-icons/io5";
import "./AntiSignatureForm.css"
import {formatUTCDate} from "../../config/DateUtils";

/**
 * @author pdoriand Sep 04, 2024
 */
interface AntiSignatureTableProps {
    antisignatures: AntiSignatureEntry[];
    refreshAntiSignatures: () => void;
    setRegionFetch: any;
    setDescriptionFetch: any;
    setSignatureTypeFetch: any;
    setModelsFetch: any;
    setTtlFetch: any;
    setLabelsFetch: any;
    setSignatureFeaturesFetch: any;
}

export function AntiSignatureTable(props: AntiSignatureTableProps) {
    const REGIONS = ["ALL", ...Object.values(Region)];
    const TYPES = ["ALL", ...Object.values(AntiSignatureType)];
    const [description, setDescription] = useState("");
    const [models, setModels] = useState("");
    const [ttl, setTtl] = useState("");
    const [labels, setLabels] = useState("");
    const [showFeatureForm, setShowFeatureForm] = useState(false);
    const [feature, setFeature] = useState("ip");
    const [value, setValue] = useState("");
    const aggregatedByCellRef = useRef(null);
    const aggregatedByModalRef = useRef(null);
    const setOnKeyDown = (event: any, setFetch: any, value: any) => {
        if (event.key === "Enter") {
            setFetch(value);
        }
    }
    useOutsideClick([aggregatedByCellRef, aggregatedByModalRef], () => setShowFeatureForm(false));
    return <div>
        {showFeatureForm && <div className="modal">
            <div ref={aggregatedByModalRef} className="modal-content">
                <div className="antisignature-form-header">
                    <div/>
                    <p className="text-center">Filter by feature</p>
                    <IoClose className="x-button" onClick={() => setShowFeatureForm(false)}/>
                </div>
                <div className="aggregated-by-container">
                    <div className="antisignature-feature-field">
                        <select onChange={(e) => setFeature(e.target.value)} defaultValue={feature}>
                            {
                                AntiSignatureFeatures.map((feature, id) => <option value={feature} key={id}>{feature}</option>)
                            }
                        </select>
                        <input defaultValue={value} onChange={(e) => setValue(e.target.value)} type="text"/>
                        <button onClick={() => {
                            props.setSignatureFeaturesFetch(new Map<String, String>([[feature, value]]));
                            setShowFeatureForm(false);
                        }}>Filter
                        </button>
                    </div>
                    <button onClick={() => {
                        props.setSignatureFeaturesFetch(new Map<String, String>());
                        setValue("");
                        setFeature("ip");
                        setShowFeatureForm(false);
                    }}>Remove filter
                    </button>
                </div>

            </div>
        </div>}
        <table className="table table-striped table-bordered">
            <thead>
            <tr>
                <th></th>
                <th>Aggregated By</th>
                <th>Region</th>
                <th>Signature Type</th>
                <th>Description</th>
                <th>Models</th>
                <th>Labels</th>
                <th>Days remaining</th>
                <th>Creation Date</th>
                <th>Author</th>
                <th>Last Modified By</th>
            </tr>
            </thead>
            <tbody>
            <tr className="filters">
                <td></td>
                <td ref={aggregatedByCellRef} onClick={() => setShowFeatureForm(true)}>
                    {value !== "" && <span>{feature} : {value}</span>}
                    {value === "" && <span>Click here to filter by feature</span>}
                </td>
                <td>
                    <select id="regionFilterSelect" defaultValue={"ALL"} onChange={(e) => props.setRegionFetch(e.target.value === "ALL" ? "" : e.target.value)}>
                        {REGIONS.map(region => <option key={region}>{region}</option>)}
                    </select>
                </td>
                <td>
                    <select id="signatureTypeFilterSelect" defaultValue={""}
                            onChange={(e) => props.setSignatureTypeFetch(e.target.value === "ALL" ? "" : e.target.value)}>
                        {TYPES.map(type => <option key={type}>{type}</option>)}
                    </select>
                </td>
                <td>
                    <input id="descriptionFilterInput" type="text" onChange={(e) => setDescription(e.target.value)}
                           onKeyDown={(e) => setOnKeyDown(e, props.setDescriptionFetch, description)}/>
                </td>
                <td>
                    <input id="modelsFilterInput" type="text" onChange={(e) => setModels(e.target.value)} onKeyDown={(e) => setOnKeyDown(e, props.setModelsFetch, models)}/>
                </td>
                <td>
                    <input id="labelsFilterInput" type="text" onChange={(e) => setLabels(e.target.value)} onKeyDown={(e) => setOnKeyDown(e, props.setLabelsFetch, labels)}/>
                </td>
                <td>
                    <input id="ttlFilterInput" type="number" onChange={(e) => setTtl(e.target.value)} onKeyDown={(e) => setOnKeyDown(e, props.setTtlFetch, ttl)}/>
                </td>
                <td></td>
                <td></td>
                <td></td>
            </tr>
            {
                props.antisignatures?.map((antiSignature, id) => {
                        const daysLeft = convertTtlToDaysRemaining(antiSignature)
                        return <tr key={id}>
                            <AntiSignatureButtons refreshAntiSignatures={props.refreshAntiSignatures} antisignature={antiSignature}/>
                            <td><span>{Object.entries(antiSignature.aggregatedBy).map((entry) => entry[0] + ": " + entry[1]).join(" ; ")}</span></td>
                            <td><span>{antiSignature.region}</span></td>
                            <td><span>{antiSignature.signatureType}</span></td>
                            <td><span>{antiSignature.description}</span></td>
                            <td><span>{antiSignature.models && Array.from(antiSignature.models).join(" ")}</span></td>
                            <td><span>{antiSignature.labels && Array.from(antiSignature.labels).join(" ")}</span></td>
                            <td><span>{daysLeft < 0 ? "Expired" : daysLeft}</span></td>
                            <td><span>{formatUTCDate(antiSignature.creationTime)}</span></td>
                            <td><span>{antiSignature.author || "Unknown"}</span></td>
                            <td><span>{antiSignature.lastModifiedBy || "Unknown"}</span></td>
                        </tr>
                    }
                )
            }
            </tbody>
        </table>
    </div>
}